import React from 'react';
import SimpleMarkdown from 'simple-markdown';
import SupplementRenderer from 'client/Supplement/SupplementRenderer.react';

/**
 * RegExp _string_ shared for the creation of various Supplment RegExps.
 * @example [s:<uuid>(:<additional_attributes)]
 * @example [s:edc918e5-a42e-41e4-b803-bb52edabe3a4:x=10:y=20:downloadable]
 */
export const supplementRegexBaseString = `\\[s:([a-zA-Z0-9-]+)(?::([^\n\\]]+))?\\]`;

const regex = new RegExp(`^${supplementRegexBaseString}`);

export const supplementRegexGlobal = new RegExp(supplementRegexBaseString, 'g');

interface SupplementAttributes {
  imageSize: string | null;
  x: string | null;
  y: string | null;
  width: string | null;
  height: string | null;
  downloadable: string | boolean | null;
  viewable: string | boolean | null;
  [key: string]: string | boolean | null;
}

interface ParsedSupplement {
  id: string;
  token: string;
  attributes: SupplementAttributes;
}

/**
 * Parse a supplement RegExp match converting it into our standard object.
 */
function parseMatch([token, id, rawAttributes]: readonly string[]): ParsedSupplement {
  const attributes =
    rawAttributes &&
    rawAttributes
      .split(':')
      .filter(Boolean)
      .reduce<Record<string, string | boolean>>((acc, attributeStr) => {
        // A segment made only of `=` matches nothing, so the assertion threw on a
        // malformed supplement token and blanked the rendered content.
        const groups = attributeStr.match(/(?<key>[^=]+)=?(?<value>[^\n]+)?/)?.groups;
        if (!groups) {
          return acc;
        }
        const {key, value} = groups;
        acc[key] = value ?? true;
        return acc;
      }, {});

  return {
    id,
    token,
    attributes: {
      // Default values to pass to the renderer
      imageSize: null,
      x: null,
      y: null,
      width: null,
      height: null,
      downloadable: null,
      viewable: null,
      // Default values end
      ...attributes
    }
  };
}

const supplementRuleBase = {
  order: SimpleMarkdown.defaultRules.paragraph.order - 0.5,
  match: (source: string) => regex.exec(source),
  parse: (capture: readonly string[]) => {
    return {
      /**
       * SimpleMarkdown will _technically_ specify the `type` here, but since it is *REQUIRED* and *RESERVED*
       * we include it here to avoid conflicts. If we _did_ just spread the result of `parseMatch`, SimpleMarkdown
       * will attempt to render the parsed Node using _that_ "type", attempting to map it to a rule (ie. "image").
       */
      type: 'supplementRule',
      content: parseMatch(capture)
    };
  }
};

function buildSupplementRule(annotatable: boolean) {
  return {
    ...supplementRuleBase,
    react: (node: {content: ParsedSupplement}) => {
      return (
        <SupplementRenderer
          id={node.content.id}
          key={node.content.token}
          attributes={node.content.attributes}
          annotatable={annotatable}
        />
      );
    }
  };
}

export const supplementRule = buildSupplementRule(true);

/** For answer-material hosts (chips, ordering items): their supplements never take whole-supplement marks. */
export const nonAnnotatableSupplementRule = buildSupplementRule(false);
/**
 * Parse a string for supplement matches, then parse the match.
 * @param str The string to parse.
 */
export function parse(str: string): ParsedSupplement {
  const nonAnchoredRegex = new RegExp(supplementRegexBaseString);
  return parseMatch(nonAnchoredRegex.exec(str)!);
}
